import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import TextField from '@mui/material/TextField';
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import DataGrid from "../../shared/DataGrid"
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import CircularProgress from "@mui/material/CircularProgress";
import SendIcon from "@mui/icons-material/Send";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import MergeIcon from '@mui/icons-material/Merge';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListBulletedAddIcon from '@mui/icons-material/FormatListBulletedAdd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SellIcon from '@mui/icons-material/Sell';
import { Link } from "react-router-dom";
import { PRIORITIES } from "../../../../../constants/Consult";
import { Chip, Popover, Box, MenuItem, Checkbox, FormControlLabel, InputAdornment } from "@mui/material";
import PdfPreview from "../../shared/PdfPreview";
import Confirmation from "../../../../../common/Comfirmation";
import SendDialog from "../../shared/sendPackage"
import MergeDialog from "../../shared/MergeDialog"
import DetailPanelContent from "../../shared/DetailPanelContent"
import { ModalityWorklistDialog } from "../../shared/ModalityWorklist"
import ScheduleSlotDialog from "../../../Schedule/ScheduleSlotDialog"
import ConsultAlertDialog from "../../shared/ConsultAlertDialog"
import dayjs from 'dayjs'
import {
  GetStudy,
  GetStudyCount,
  Subscribe,
  Unsubscribe,
  SendStudy,
  SendMobile,
  DeleteStudy,
  ClearStudy,
  DuplicateStudy,
  GetToken,
  UpdateStudyTags,
  GetOrgTags,
  zipApi
} from "../../../../../api";

let abortSignal, abortCountSignal

const underScoreSort = (a, b) => {
  const e = a.startsWith("_"),
    f = b.startsWith("_");
  return e && !f ? -1 : !e && f ? 1 : 0
}

const currentDate = new Date()
const sevenDaysAgoTimestamp = currentDate.getTime() - (7 * 24 * 60 * 60 * 1000)

const debounceTimers = {}
const debounceTime = 1000
const debounceSubTime = debounceTime * 2

const TagCell = ({ tags, availableTags, canDelete, onAdd, onDelete }) => {
  const [anchor, setAnchor] = React.useState(null)
  const label = tags.length === 0 ? '+' : tags.length === 1 ? tags[0].name : `${tags[0].name} +${tags.length - 1}`
  return (
    <>
      <Tooltip title={tags.length > 1 ? tags.map(t => t.name).join(', ') : ''}>
        <Chip
        label={label}
        size="small"
        variant={tags.length ? 'filled' : 'outlined'}
        onClick={e => setAnchor(e.currentTarget)}
        sx={{ maxWidth: 140, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
      />
      </Tooltip>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 1, minWidth: 180, maxWidth: 280 }}>
          {tags.map(t => (
            <Box key={t.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Chip
                label={t.name}
                size="small"
                onDelete={canDelete(t) ? () => onDelete(t) : undefined}
                sx={{ maxWidth: 220 }}
              />
            </Box>
          ))}
          {availableTags.length > 0 && [
            tags.length > 0 && <Box key="divider" sx={{ borderTop: '1px solid', borderColor: 'divider', my: 0.5 }} />,
            ...availableTags.map(t => (
              <MenuItem key={t} dense onClick={() => { onAdd(t); setAnchor(null) }}>{t}</MenuItem>
            ))
          ]}
        </Box>
      </Popover>
    </>
  )
}

const Clinic = ({ user, view, filter, access, socketStatus, hasSchedule, hasModalitySchedule }) => {
  const [studies, setStudies] = useState([])
  const [merging, setMerging] = useState()
  const [dialogState, setDialogState] = useState({
    open: false,
    studyId: null,
  })
  const [sendDialogState, setSendDialogState] = useState({})
  const [mergeDialogState, setMergeDialogState] = useState({})
  const [modalityDialogState, setModalityDialogState] = useState({})
  const [scheduleDialogState, setScheduleDialogState] = useState({ open: false, study: null })
  const [consultAlertState, setConsultAlertState] = useState({ open: false, text: '', onConfirm: null })
  const [apiParams, setApiParams] = useState({ sort: 'MainDicomTags.StudyDate', order: 'desc', limit: '', offset: '' })
  const [loading, setLoading] = useState(false)
  const [rowCount, setRowCount] = useState(0)
  const [pageSize] = useState(50)
  const [search, setSearch] = useState({})
  const [orderedFields, setOrderedFields] = useState()
  const [subs, setSubs] = useState([])
  const [orgTags, setOrgTags] = useState([])
  const [popover, setPopover] = useState()

  const subsRef = useRef()
  subsRef.current = subs

  const org = access.org?.id;

  const [deleteConfirmation, setdeleteConfirmation] = useState({
    open: false,
    text: "",
    studyId: undefined,
    api: undefined
  })

  const apiRef = useGridApiRef();
  const [initialState, setInitialState] = React.useState();
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});

  const saveSnapshot = React.useCallback(() => {
    if (apiRef.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(`DataGridState-${view}-${filter}`, JSON.stringify(currentState));
    }
  }, [apiRef, view, filter]);



  useLayoutEffect(() => {
    const oldStateFromLocalStorage = localStorage?.getItem('clinicDataGridState')
    const stateFromLocalStorage = localStorage?.getItem(`DataGridState-${view}-${filter}`) || oldStateFromLocalStorage
    const state = stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {
      sorting: { sortModel: [{ field: "MainDicomTags.StudyDate", sort: "desc" }] },
    }
    setInitialState(state)
    setColumnVisibilityModel({ ...state.columns?.columnVisibilityModel, 'scheduleSlot.date': false })
    if (state.columns?.orderedFields)
      setOrderedFields(state.columns.orderedFields.sort(underScoreSort))

    if (state.pagination?.paginationModel)
      onPageChange(state.pagination.paginationModel)

    // handle refresh and navigating away/refreshing
    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot, view, filter]);

  useEffect(() => {
    GetOrgTags(org).then(setOrgTags).catch(() => setOrgTags([]))
  }, [org])

  useEffect(() => {
    if (!hasSchedule && !hasModalitySchedule)
      setColumnVisibilityModel(m => ({ ...m, 'scheduleSlot.date': false }))
  }, [hasSchedule, hasModalitySchedule]);

  const deleteConfirm = () => {
    const { api, studyId } = deleteConfirmation
    api(studyId).then(() => {
      if (api === DeleteStudy) {
        setStudies((i) => {
          let studies = [...i];
          studies.splice(studies.findIndex((x) => x.id === studyId), 1)
          return studies;
        })
      } else {
        getStudy()
      }
      setdeleteConfirmation({ open: false, text: "", studyId: undefined, api: undefined })
    }).catch(e => e)
  }
  const deleteCancel = () => {
    setdeleteConfirmation(
      (i) => (i = { open: false, text: "", studyId: undefined, api: undefined })
    )
  }
  const deleteClick = (studyId, studyName, event, weekOld) => {
    if (weekOld || event.shiftKey)
      return DeleteStudy(studyId)
    setdeleteConfirmation(
      (i) =>
      (i = {
        open: true,
        text: `Are you sure you want to delete study: ${studyName}?`,
        studyId,
        api: DeleteStudy
      })
    )
  }
  const clearClick = (studyId, studyName) => {
    setdeleteConfirmation(
      (i) =>
      (i = {
        open: true,
        text: `Are you sure you want to clear study: ${studyName}?`,
        studyId,
        api: ClearStudy
      })
    )
  }

  const onSortChange = params => {
    if (rowCount > pageSize)
      setApiParams(x => ({ ...x, sort: (params.length && params[0].field) || '', order: (params.length && params[0].sort) || '' }))
  }
  const onPageChange = params => {
    setApiParams(x => ({ ...x, limit: params.pageSize || '', offset: params.page || '' }))
  }

  const sendOnClose = () => setSendDialogState({})
  const sendOnSend = () => setSendDialogState({})
  const sendPackage = useCallback(params => setSendDialogState({ ...params, open: true, onClose: sendOnClose, onSend: sendOnSend }), [])

  const medDream = StudyInstanceUID => {
    GetToken({ data: StudyInstanceUID }).then(result => {
      let { accessToken } = result
      accessToken && window.open(`https://dream.pacs.corridor.vet/?token=${accessToken}`, '_blank')
    }).catch(e => e)
    return false
  }

  const getStudyCallback = useCallback(() => {
    if (false && mergeDialogState.open); // Don't warn for mergeDialogState, need it for mergeOnComplete
    abortSignal = new AbortController()
    abortCountSignal = new AbortController()
    GetStudy(null, { ...apiParams, view, filter, org, search }, abortSignal.signal).then(result => {
      setStudies(() => result || [])
      setLoading(false)
    }).catch(e => e) // Catch incase of abort
    GetStudyCount({ view, filter, org, search }, abortCountSignal.signal).then(setRowCount).catch(e => e)
  }, [apiParams, view, filter, org, search, mergeDialogState.open])

  const getStudy = useCallback(() => {
    abortSignal && abortSignal.abort()
    abortCountSignal && abortCountSignal.abort()
    clearTimeout(debounceTimers.refresh)
    debounceTimers.refresh = setTimeout(getStudyCallback, debounceTime)
  }, [getStudyCallback])

  useEffect(() => {
    setLoading(true)
    getStudy()
    return () => {
      if (abortSignal)
        abortSignal.abort()
    }
  }, [view, filter, org, apiParams, search, getStudy, socketStatus]);

  useEffect(() => {
    const type = (['pending', 'complete'].includes(filter) && filter) || 'insert'
    Subscribe({ coll: 'studies', type }, getStudy)
    return () => socketStatus && Unsubscribe({ coll: 'studies', type })
  }, [filter, org, getStudy, socketStatus])

  const getStudyByIdCallback = useCallback(id => {
    GetStudy(id).then(data => {
      if (data.id) {
        setStudies(studies => {
          const copyStudies = [...studies]
          const i = copyStudies.findIndex((x) => x.id === data.id)
          if (i >= 0) {
            if (data.sentBy || data.deleted) copyStudies.splice(i, 1)
            else copyStudies[i] = { ...data }
          }
          return copyStudies
        })
      }
    }).catch(e => e)
  }, [])

  const getStudyById = useCallback(id => {
    clearTimeout(debounceTimers[id])
    debounceTimers[id] = setTimeout(() => getStudyByIdCallback(id), debounceSubTime)
  }, [getStudyByIdCallback])

  useEffect(() => {
    if (socketStatus)
      setSubs(subs => {
        const newSubs = []
        for (const study of studies) {
          newSubs.push(study.id)
          if (!subs || !subs.includes(study.id))
            Subscribe({ coll: "studies", type: "update", id: study.id }, resp => getStudyById(resp.id))
        }
        if (subs)
          for (const sub of subs)
            if (!newSubs.includes(sub))
              Unsubscribe({ coll: "studies", type: "update", id: sub })
        return newSubs
      })
    else setSubs([])
  }, [studies, getStudyById, socketStatus])

  useEffect(() => {
    return () => {
      for (const sub of subsRef.current)
        Unsubscribe({ coll: "studies", type: "update", id: sub })
      setSubs([])
    }
  }, [apiParams, view, filter, org, search])

  /* Dynamic Section */
  const sendStudy = useCallback(id => {
    SendStudy(id).then(() => getStudy()).catch(e => e)
  }, [getStudy])
  const sendMobile = useCallback(id => {
    SendMobile(id).then(() => getStudy()).catch(e => e)
  }, [getStudy])

  const sendWithAlert = useCallback((row, fn) => {
    const ca = row.org?.settings?.consultAlert
    const now = Date.now()
    if (ca?.text && ca?.start && ca.start <= now && (!ca.end || ca.end > now))
      setConsultAlertState({ open: true, text: ca.text, onConfirm: fn })
    else
      fn()
  }, [])

  const mergeOnClose = () => setMergeDialogState({})
  const mergeOnSend = () => setMergeDialogState({})
  //const mergeOnComplete = () => {}
  const mergeStudies = useCallback(row => setMerging(m => {
    if (m && row.PatientMainDicomTags?.PatientID === m.PatientMainDicomTags?.PatientID) {
      if (row.id !== m.id)
        setMergeDialogState({ merge: [{ ...m }, { ...row }], open: true, onClose: mergeOnClose, onSend: mergeOnSend, onSendComplete: getStudy })
      return
    }
    return row
  }), [getStudy])

  const mwlOnClose = () => setModalityDialogState({})
  const mwlOnSend = () => setModalityDialogState({})
  const editModality = useCallback(params => setModalityDialogState({ ...params, open: true, onClose: mwlOnClose, onSend: mwlOnSend, aetitle: Array.isArray(access.org.aetitle) ? access.org.aetitle[0] : access.org.aetitle }), [access.org.aetitle])
  const openScheduleDialog = useCallback(study => setScheduleDialogState({ open: true, study }), [])
  const closeScheduleDialog = useCallback(refresh => { setScheduleDialogState({ open: false, study: null }); if (refresh) getStudy() }, [getStudy])
  /* End Dynamic Section */

  const openPopover = useCallback((event, type) => setPopover({ filter: type, anchor: event.currentTarget }), [])

  const columnSort = useCallback((a, b) => (orderedFields && orderedFields.indexOf(a.field) - orderedFields.indexOf(b.field)) || 0, [orderedFields])

  const getDetailPanelContent = useCallback(
    ({ row }) => (
      <DetailPanelContent medDream={medDream} setPdfDialogState={setDialogState} org={access.org.id} row={row} showPatientInfo />
    ),
    [access.org.id, setDialogState]
  )


    
  const columns = useMemo(() => {

    // eslint-disable-next-line no-unused-vars
    const renderActionsCell = params => {
      const { row } = params;
      const sent = row.sentBy ? true : ((row.mobile || row.imaging) && row.requestBy && !row.instanceCount ? true : false);
      const hasPrio = row.org?.priorities && row.org.priorities.length
      const weekOld = new Date(row.createdAt || row.updatedAt) < sevenDaysAgoTimestamp
      const mobileRequest = (row.mobile || row.imaging) && !row.instanceCount
      const completeRequired =
        row.consult &&
        row.org &&
        ((hasPrio && (row.priority || row.priority === 0 || row.priorityRef)) || !hasPrio) &&
        ((user.settings?.orgSend && (row.associateOrg?.id === access.org.id || row.aetitle === access.org.aetitle)) || !user.settings?.orgSend) &&
        (row.numImages || row.numImages === 0) &&
        row.MainDicomTags.StudyDescription &&
        row.PatientMainDicomTags.ResponsiblePerson &&
        row.PatientMainDicomTags.PatientName &&
        row.PatientMainDicomTags.PatientID &&
        row.PatientMainDicomTags.PatientSex &&
        row.PatientMainDicomTags.PatientBirthDate &&
        row.PatientMainDicomTags.PatientSpeciesDescription &&
        row.PatientMainDicomTags.PatientBreedDescription &&
        row.PatientMainDicomTags.PatientWeight &&
        (!row.org?.settings?.requireBilling || row.billing?.length) &&
        (!row.org?.settings?.requirePhysician || row.MainDicomTags.ReferringPhysicianName)

      return (
        <>
          <Tooltip title="Download">
            <IconButton
              aria-label="download"
              component={Link}
              to={`${zipApi}studies/${row.id}/zip`}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Viewer">
            <IconButton
              aria-label="view"
              disabled={!row.Series?.length}
              onClick={() => medDream(row.MainDicomTags.StudyInstanceUID)}
            >
              <BrokenImageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Rename">
            <IconButton
              component={Link}
              to={`/studies/${row.id}`}
              aria-label="rename"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          {access.org.settings?.modalityWorklist && row.request && !row.completeBy && (
            <Tooltip title={row.modalityWorklist ? 'Edit Modality' : 'Create Modality'}>
              <IconButton
                aria-label="modality"
                onClick={() => editModality({study: row})}
              >
                {row.modalityWorklist ? <FormatListBulletedIcon /> : <FormatListBulletedAddIcon />}
              </IconButton>
            </Tooltip>
          )}
          {(hasSchedule || hasModalitySchedule) && row.mobile && row.requestBy && !row.completeBy && (
            <Tooltip title={row.scheduleSlot || row.modalitySlot ? 'Reschedule' : 'Schedule'}>
              <IconButton
                aria-label="schedule"
                onClick={() => openScheduleDialog(row)}
              >
                <AccessTimeIcon color={row.scheduleSlot || row.modalitySlot ? 'primary' : 'inherit'} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Duplicate">
            <IconButton
              onClick={() => DuplicateStudy(row.id)}
              aria-label="duplicate"
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          {(user.settings?.unsafeMerge || (!row.claimBy && !row.duplicate)) && (
            <Tooltip title="Merge">
              <IconButton
                aria-label="merge"
                onClick={() => mergeStudies(row)}
              >
                <MergeIcon color={
                  (merging && (
                    (merging.id === row.id && 'warning') ||
                    (merging.PatientMainDicomTags?.PatientID === row.PatientMainDicomTags?.PatientID && 'primary')
                  )) || 'inherit'
                } />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Consult">
            <span>
              <IconButton
                component={Link}
                to={`/studies/${row.id}/consult`}
                aria-label="consult"
                disabled={row.sentBy}
              >
                <AssignmentIcon />
              </IconButton>
            </span>
          </Tooltip>
          {!sent && (
            <Tooltip title={mobileRequest ? 'Request' : 'Send'}>
              <span>
                <IconButton
                  aria-label={mobileRequest ? 'request' : 'send'}
                  onClick={() => sendWithAlert(row, () => (mobileRequest ? sendMobile : sendStudy)(`${row.id}`))}
                  disabled={!completeRequired}
                >
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {row.completeBy && (
            <Tooltip title="View Report">
              <IconButton
                aria-label="view report"
                onClick={() =>
                  setDialogState({ open: true, studyId: `${row.id}` })
                }
              >
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
          )}
          {!row.completeBy && !row.claimBy && ((row.sentBy && (
            <Tooltip title="Clear">
              <span>
                <IconButton
                  aria-label="clear"
                  onClick={() =>
                    clearClick(
                      row.id,
                      `${row.PatientMainDicomTags.PatientName || ""} ${row.MainDicomTags.StudyDescription || ""
                        }`.trim()
                    )
                  }
                >
                  <SettingsBackupRestoreIcon />
                </IconButton>
              </span>
            </Tooltip>
          )) || (
              <Tooltip title={weekOld ? 'Instant Delete' : 'Delete'}>
                <span>
                  <IconButton
                    aria-label="delete"
                    color={weekOld ? 'info' : 'default'}
                    onClick={e =>
                      deleteClick(
                        row.id,
                        `${row.PatientMainDicomTags.PatientName || ""} ${row.MainDicomTags.StudyDescription || ""
                          }`.trim(),
                        e, weekOld
                      )
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          <Tooltip title="Email Package">
            <span>
              <IconButton
                aria-label="email package"
                onClick={() => sendPackage({ studyId: row.id, disableReport: !row.completeBy })}
              >
                <ForwardToInboxIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )
    }

    const hasTags = orgTags.length > 0

    const c = [{
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      minWidth: 11 * 40 + 20,
      fontWeight: 600,
      sortable: false,
      resizable: false,
      hideable: false,
      filterable: false,
      disableColumnSelector: true,
      pinned: 'left',
      renderCell: renderActionsCell,
    },
    {
      field: "priority",
      headerName: "Priority",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      valueGetter: (value, row) => {
        const pro = row.priority || 0
        return row.priorityRef?.name || PRIORITIES.find(e => e.id === pro)?.name || ''
      },
    },
    {
      field: "MainDicomTags.StudyDate",
      headerName: "Study Date",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      minWidth: 150,
      type: "dateTime",
      valueGetter: (value, row) => new Date(row.MainDicomTags.StudyTimestamp),
      renderCell: params => {
        const outputOptions = {
          year: "2-digit",
          month: "numeric",
          day: "numeric",
          hour: params.row.MainDicomTags.StudyTime ? "numeric" : undefined,
          minute: params.row.MainDicomTags.StudyTime ? "numeric" : undefined,
        }
        const timestamp = new Date(params.value)
        return timestamp.toLocaleString("en-US", outputOptions)
      },
    },
    {
      field: "sentAt",
      headerName: "Request Date",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      minWidth: 150,
      type: "dateTime",
      valueGetter: (value, row) => row.sentAt && dayjs(row.sentAt).toDate(),
      renderCell: (params) => {
        const outputOptions = {
          year: "2-digit",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          timeZone: params.row.org?.timezone || 'America/Los_Angeles'
        }
        return (params.value && params.value.toLocaleString("en-US", outputOptions)) || ''
      }
    },
    {
      field: "scheduleSlot.date",
      headerName: "Scheduled Date",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      minWidth: 150,
      type: "dateTime",
      hideable: hasSchedule || hasModalitySchedule,
      valueGetter: (value, row) => {
        const d = row.scheduleSlot?.date || row.modalitySlot?.date
        return d ? dayjs(d).toDate() : null
      },
      renderCell: (params) => {
        const outputOptions = {
          year: "2-digit",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          timeZone: params.row.org?.timezone || 'America/Los_Angeles'
        }
        return (params.value && params.value.toLocaleString("en-US", outputOptions)) || ''
      }
    },
    {
      field: "PatientMainDicomTags.PatientID",
      headerName: "Patient ID",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      valueGetter: (value, row) => row.PatientMainDicomTags.PatientID,
    },
    {
      field: "PatientMainDicomTags.PatientName",
      headerName: "Patient Name",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      minWidth: 200,
      valueGetter: (value, row) => row.PatientMainDicomTags.PatientName,
    },
    {
      field: "PatientMainDicomTags.ResponsiblePerson",
      headerName: "Owner Name",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      minWidth: 200,
      valueGetter: (value, row) => row.PatientMainDicomTags.ResponsiblePerson,
    },
    {
      field: "associateOrg.name",
      headerName: "Requested By",
      headerAlign: "center",
      align: "center",
      minWidth: 250,
      sortable: false,
      filterable: false,
      fontWeight: 600,
      valueGetter: (value, row) => {
        if (row.associateOrg)
          return row.associateOrg?.name
        if (row.aetitle) {
          const orgFind = user.access.find(o=>o.org.aetitle===row.aetitle);
          if (orgFind)
            return orgFind.org.name
        }
        return ''
      } 
    },
    {
      field: "modalities",
      headerName: "Modality",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      sortable: true,
      filterable: true,
      valueGetter: (value, row) => row.modalities?.join(', ') || '',
    },
    {
      field: "MainDicomTags.StudyDescription",
      headerName: "Study Description",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      minWidth: 200,
      valueGetter: (value, row) => row.MainDicomTags.StudyDescription,
    },
    {
      field: "instanceCount",
      headerName: "Images",
      headerAlign: "center",
      align: "center",
      fontWeight: 600,
      sortable: true,
      filterable: true,
      valueGetter: (value, row) => row.instanceCount,
      renderCell: params => params.value + (params.row.media && params.row.media.length ? `+${params.row.media.length}` : '') + (params.row.numImages ? ` of ${params.row.numImages}` : '')
    },
    ...(hasTags ? [{
      field: 'tags',
      headerName: 'Tags',
      headerAlign: 'center',
      align: 'center',
      fontWeight: 600,
      minWidth: 160,
      sortable: false,
      filterable: false,
      valueGetter: (value, row) => row.tags?.map(t => t.name).join(', ') || '',
      renderCell: params => {
        const tags = params.row.tags || []
        const availableTags = orgTags.filter(t => !tags.some(e => e.name === t))
        const canDelete = tag => access.admin || tag.view === view
        const updateTag = (action, name, tagView) => {
          UpdateStudyTags(params.row.id, { action, name, view: tagView || view }).then(result => {
            setStudies(s => s.map(r => r.id === params.row.id ? { ...r, tags: result.tags } : r))
          }).catch(e => e)
        }
        return (
          <TagCell
            tags={tags}
            availableTags={availableTags}
            canDelete={canDelete}
            onAdd={name => updateTag('add', name, view)}
            onDelete={tag => updateTag('remove', tag.name)}
          />
        )
      }
    }] : [])
  ]

    return c.sort(columnSort)

  }, [columnSort, sendStudy, sendMobile, sendWithAlert, sendPackage, merging, mergeStudies, editModality, openScheduleDialog, access.org, access.admin, user, hasSchedule, hasModalitySchedule, view, orgTags])

  const handleColumnOrderChange = useCallback(model => {
    setOrderedFields(Object.entries(model).sort((a, b) => a[1] - b[1]).map(([field]) => field))
  }, [])

  if (!initialState) {
    return <CircularProgress />;
  }

  return (
    <Paper style={{ height: 700, width: "100%" }}>
      <TextField
        id="search"
        label="Search"
        type="text"
        variant="filled"
        fullWidth
        value={search.query || ''}
        onChange={(e) => setSearch(x => ({...x, query: e.target.value}))}
        InputProps={orgTags.length > 0 ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={(e) => openPopover(e, 'tag')}>
                <SellIcon color={search?.tags?.length ? 'primary' : 'inherit'} fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        } : undefined}
      />
      <Popover
        open={Boolean(popover?.anchor)}
        anchorEl={popover?.anchor}
        onClose={() => setPopover()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box p={2} minWidth={200}>
          {popover?.filter === 'tag' && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {orgTags.map(t => (
                <FormControlLabel
                  key={t}
                  label={t}
                  control={
                    <Checkbox
                      size="small"
                      checked={search?.tags?.includes(t) || false}
                      onChange={e => setSearch(x => {
                        const tags = x.tags ? [...x.tags] : []
                        e.target.checked ? tags.push(t) : tags.splice(tags.indexOf(t), 1)
                        return { ...x, tags: tags.length ? tags : undefined }
                      })}
                    />
                  }
                />
              ))}
            </Box>
          )}
        </Box>
      </Popover>
      <DataGrid
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        rows={studies}
        pageSize={pageSize}
        sx={{ minWidth: 650 }}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={loading}
        filterMode="server"
        sortingMode={rowCount > pageSize ? 'server' : 'client'}
        paginationMode="server"
        onSortModelChange={onSortChange}
        onPaginationModelChange={onPageChange}
        disableColumnFilter={true}
        pagination={true}
        disableChildrenFiltering={true}
        disableChildrenSorting={true}
        disableAggregation={true}
        disableRowGrouping={true}
        rowCount={rowCount}
        apiRef={apiRef}
        initialState={initialState}
        onColumnOrderModelChange={handleColumnOrderChange}
        getDetailPanelContent={getDetailPanelContent}
      //rowBuffer={100} // Amount of rows allowed to be expanded before deleting old renders
      />
      <PdfPreview
        open={dialogState.open}
        studyId={dialogState.studyId}
        setDialogState={setDialogState}
      />
      <Confirmation
        open={deleteConfirmation.open}
        onCancel={deleteCancel}
        onConfirm={deleteConfirm}
      >
        {deleteConfirmation.text}
      </Confirmation>
      <SendDialog dialogState={sendDialogState} />
      <MergeDialog dialogState={mergeDialogState} />
      <ModalityWorklistDialog dialogState={modalityDialogState} />
      <ScheduleSlotDialog open={scheduleDialogState.open} study={scheduleDialogState.study} access={access} onClose={closeScheduleDialog} />
      <ConsultAlertDialog
        open={consultAlertState.open}
        text={consultAlertState.text}
        onClose={() => { const fn = consultAlertState.onConfirm; setConsultAlertState({ open: false, text: '', onConfirm: null }); fn && fn() }}
      />
    </Paper>
  );
};

export default Clinic;
